import { useEffect, useMemo, useState } from 'react'
import { Redirect } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { resetEvaluations, setInformInfoInEvaluations } from '../../redux/evaluations'
import { resetImages } from '../../redux/images'
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom'
import { PatientInformation } from '../../components'
import { USER_STATUS } from '../../const'
import { toggleToNewStatus } from '../../redux/user'
import { setErrorSnackbar } from '../../redux/snackbar'
import { Container, ReportWrapper, CaseId, ReadOnlyMessageContainer } from './styles'
import GenerateReport from '../../components/GenerateReport'
import { LoadingScreen } from '../../components/LoadingScreen'
import { useInform } from '../../hooks/Inform/useInform'
import { useOpenPDF } from '../../hooks/useOpenPDF'
import { ReportSended } from './ReportSended'
import { NewReportModal } from '../../components/Modal/CustomModals/NewReportModal'
import GenericModal from '../../components/Modal/GenericModal'
import { useModal } from '../../hooks/useModal'
import { useUpdateUserEvaluations } from '../../hooks/useUpdateUserEvaluations'
import { pdfDisponible } from '../../utils/pdfDisponible'
import OphthalmologyReportForm from '../../components/OphthalmologyReportForm'
import {
  getInitialReportFormState,
  toInformPayload,
  RECAPTURE_ACTION,
  isRecaptureAction
} from '../../components/OphthalmologyReportForm/helpers'

const STUDY_TYPE = {
  0: 'Examen binocular',
  1: 'Examen monocular ojo derecho',
  2: 'Examen monocular ojo izquierdo'
}

const MANUAL_QUALITY_CODE_TO_VALUE = {
  0: 'mala',
  1: 'buena',
  2: 'aceptable'
}


const getEyeKeysByStudyType = (studyType) => {
  const parsedStudyType = Number(studyType)
  if (parsedStudyType === 1) return ['rightEye']
  if (parsedStudyType === 2) return ['leftEye']
  return ['rightEye', 'leftEye']
}

const getEyesWithAllImagesInPoorQuality = (imagesState, studyType) => {
  if (!imagesState) return []

  const eyesWithPoorQuality = []

  getEyeKeysByStudyType(studyType).forEach((eyeKey) => {
    const eyeImages = (imagesState[eyeKey] && imagesState[eyeKey].images) || []
    const uploadedEyeImages = eyeImages.filter((image) => image && image.image)
    if (!uploadedEyeImages.length) return

    const allImagesInPoorQuality = uploadedEyeImages.every((image) => Number(image.status) === 0)
    if (!allImagesInPoorQuality) return

    eyesWithPoorQuality.push({
      eyeKey,
      eyeLabel: eyeKey === 'leftEye' ? 'izquierdo' : 'derecho',
      images: uploadedEyeImages
    })
  })

  return eyesWithPoorQuality
}

const buildManualQualityFromImages = (imagesState) => {
  const qualityMap = {}
  if (!imagesState) return qualityMap

  const processEyeImages = (eyeKey, eyeLabel) => {
    const eyeImages = (imagesState[eyeKey] && imagesState[eyeKey].images) || []
    eyeImages
      .filter((image) => image && image.image && image.manualStatus !== null && image.manualStatus !== undefined)
      .forEach((image) => {
        const mappedQuality = MANUAL_QUALITY_CODE_TO_VALUE[Number(image.manualStatus)]
        if (!mappedQuality) return

        qualityMap[image.id] = {
          value: mappedQuality,
          eye: eyeLabel,
          position: image.position || 0
        }
      })
  }

  processEyeImages('rightEye', 'right')
  processEyeImages('leftEye', 'left')

  return qualityMap
}

const buildEyeQualityCounters = (qualityByImage = {}) => {
  const initialCounters = { buena: 0, aceptable: 0, mala: 0 }
  const countersByEye = {
    right: { ...initialCounters },
    left: { ...initialCounters }
  }

  Object.values(qualityByImage).forEach((quality) => {
    if (!quality) return

    const eyeKey = quality.eye === 'left' ? 'left' : 'right'
    const qualityKey = MANUAL_QUALITY_CODE_TO_VALUE[Number(quality.calidad_manual)]
    if (!qualityKey) return

    countersByEye[eyeKey][qualityKey] += 1
  })

  return countersByEye
}

const buildEyeQualitySummary = (eyeCounters) => {
  if (!eyeCounters) return ''
  const total = eyeCounters.buena + eyeCounters.aceptable + eyeCounters.mala
  if (total === 0) return ''

  return `Calidad de las imágenes capturadas de este ojo: ${eyeCounters.buena} de buena calidad, ${eyeCounters.aceptable} de calidad aceptable, ${eyeCounters.mala} de mala calidad.`
}

const prependManualQualitySummaryToFindings = (payload) => {
  if (!payload || !payload.calidad_imagenes || !Object.keys(payload.calidad_imagenes).length) return payload

  const countersByEye = buildEyeQualityCounters(payload.calidad_imagenes)
  const rightEyeSummary = buildEyeQualitySummary(countersByEye.right)
  const leftEyeSummary = buildEyeQualitySummary(countersByEye.left)

  if (rightEyeSummary) {
    payload.hallazgos_ojo_derecho = `${rightEyeSummary}\n\n${(payload.hallazgos_ojo_derecho || '').trim()}`
  }

  if (leftEyeSummary) {
    payload.hallazgos_ojo_izquierdo = `${leftEyeSummary}\n\n${(payload.hallazgos_ojo_izquierdo || '').trim()}`
  }

  return payload
}

const appendNoRecaptureJustification = ({
  recommendations = '',
  justification = ''
}) => {
  const normalizedJustification = `${justification || ''}`.trim()
  if (!normalizedJustification) return recommendations

  const justificationBlock = [
    'Justificación de envío sin recaptura por mala calidad IA:',
    normalizedJustification
  ].join('\n')

  const normalizedRecommendations = `${recommendations || ''}`.trim()
  if (!normalizedRecommendations) return justificationBlock

  return `${normalizedRecommendations}\n\n${justificationBlock}`
}

const formatEyeList = (eyes = []) => {
  if (eyes.length === 0) return ''
  if (eyes.length === 1) return `del ojo ${eyes[0]}`
  if (eyes.length === 2) return `de los ojos ${eyes[0]} y ${eyes[1]}`
  return `de los ojos ${eyes.slice(0, -1).join(', ')} y ${eyes[eyes.length - 1]}`
}

export function NewReport () {
  const { user, evaluations, images } = useSelector(state => state)
  const [reportSended, setReportSended] = useState(false)
  const [isSubmittingReport, setIsSubmittingReport] = useState(false)
  const [modalRef, openModal, closeModal] = useModal()
  const [emptyField, setEmptyField] = useState(true)
  const [selectedStudyTab, setSelectedStudyTab] = useState(0)
  const { updateUserEvaluations } = useUpdateUserEvaluations()
  const [modalType, setModalType] = useState('')
  const [showQualityRiskModal, setShowQualityRiskModal] = useState(false)
  const [qualityRiskEyes, setQualityRiskEyes] = useState([])
  const [qualityRiskImagesCount, setQualityRiskImagesCount] = useState(0)
  const [qualityRiskManualCount, setQualityRiskManualCount] = useState(0)
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()
  const { uploadReport, updateInform, cancelReport } = useInform()
  const { openPDF } = useOpenPDF()
  const isReadOnlyMode = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return params.get('mode') === 'readonly'
  }, [location.search])

  const patientPresentationMode = useMemo(() => {
    if (user.role !== 'patient' || !isReadOnlyMode) return 'editor'
    if (evaluations?.estado === 'no referido') return 'not-referred-message'
    if (evaluations?.estado === 'informado' && evaluations?.informInfo) return 'final-report'
    return 'professional-report-pending-message'
  }, [user.role, isReadOnlyMode, evaluations])

  const isPatientReadOnlyWithoutReport = patientPresentationMode === 'not-referred-message' || patientPresentationMode === 'professional-report-pending-message'

  const [reportBootstrapReady, setReportBootstrapReady] = useState(() => Boolean(evaluations))
  const [newData, setNewData] = useState(() => getInitialReportFormState({
    informInfo: evaluations && evaluations.informInfo,
    study: evaluations,
    avoidDefaults: isPatientReadOnlyWithoutReport
  }))

  useEffect(() => {
    if (!evaluations || reportBootstrapReady) return
    const initialFormState = getInitialReportFormState({
      informInfo: evaluations.informInfo,
      study: evaluations,
      avoidDefaults: isPatientReadOnlyWithoutReport
    })
    setNewData(initialFormState)
    setReportBootstrapReady(true)
  }, [evaluations])

  useEffect(() => {
    if (!reportBootstrapReady || !evaluations) return
    setNewData(prev => {
      if (prev.calidad_imagenes && Object.keys(prev.calidad_imagenes).length > 0) return prev
      const qualityFromImages = buildManualQualityFromImages(images)
      if (!Object.keys(qualityFromImages).length) return prev
      return { ...prev, calidad_imagenes: qualityFromImages }
    })
  }, [images, reportBootstrapReady, evaluations])

  const handleWarningAction = (action) => {
    if (isReadOnlyMode || isSubmittingReport) return

    if (action === 'send') {
      const riskEyes = getEyesWithAllImagesInPoorQuality(images, evaluations && evaluations.tipo)
      const selectedAction = (newData && newData.accion_inmediata) || ''

      if (riskEyes.length > 0 && !isRecaptureAction(selectedAction)) {
        const riskEyeImages = riskEyes.flatMap((riskEye) => riskEye.images)
        const manualCount = riskEyeImages.filter((image) => {
          const manualQuality = newData &&
            newData.calidad_imagenes &&
            (newData.calidad_imagenes[image.id] || newData.calidad_imagenes[String(image.id)])

          return Boolean(manualQuality && manualQuality.value)
        }).length

        setQualityRiskEyes(riskEyes.map((riskEye) => ({
          eyeKey: riskEye.eyeKey,
          eyeLabel: riskEye.eyeLabel
        })))
        setQualityRiskImagesCount(riskEyeImages.length)
        setQualityRiskManualCount(manualCount)
        setShowQualityRiskModal(true)
        return
      }
    }

    setModalType(action)
    openModal()
  }

  const modalAction = () => {
    if (isReadOnlyMode || isSubmittingReport) return
    closeModal()
    if (modalType === 'send') {
      handleSendReport()
    } else if (modalType === 'cancel') {
      handleCancelReport()
    }
  }

  const handleCloseQualityRiskModal = () => {
    setShowQualityRiskModal(false)
  }

  const handleManualImageQualityChange = (imageId, qualityValue, metadata = {}) => {
    if (isReadOnlyMode || isSubmittingReport) return
    if (!imageId) return

    setNewData((previousData) => {
      const currentQualityByImage = { ...(previousData.calidad_imagenes || {}) }

      if (!qualityValue) {
        delete currentQualityByImage[imageId]
      } else {
        currentQualityByImage[imageId] = {
          ...(currentQualityByImage[imageId] || {}),
          ...metadata,
          value: qualityValue
        }
      }

      return {
        ...previousData,
        calidad_imagenes: currentQualityByImage
      }
    })
  }

  const handleSendReport = async (noRecaptureJustification = '') => {
    if (isReadOnlyMode || isSubmittingReport) return
    setIsSubmittingReport(true)

    try {
      const payload = toInformPayload(newData)

      prependManualQualitySummaryToFindings(payload)

      payload.conducta_recomendaciones = appendNoRecaptureJustification({
        recommendations: payload.conducta_recomendaciones,
        justification: noRecaptureJustification
      })

      await updateInform(payload)
      await uploadReport()
      await updateUserEvaluations()
      dispatch(resetEvaluations())
      dispatch(resetImages())
      dispatch(toggleToNewStatus())
      setReportSended(true)
      history.push('/home')
    } catch (err) {
      console.error('Error enviando informe: ', err)
      dispatch(setErrorSnackbar('Error al enviar el informe. Por favor, intente nuevamente.'))
    } finally {
      setIsSubmittingReport(false)
    }
  }

  const handleSendWithQualityRisk = (justification) => {
    handleSendReport(justification)
  }

  const handleCancelReport = async () => {
    if (isReadOnlyMode || isSubmittingReport) return
    await cancelReport()
    await updateUserEvaluations()
    dispatch(resetEvaluations())
    dispatch(resetImages())
    dispatch(toggleToNewStatus())
    history.push('/home')
  }

  const handleSaveReport = async () => {
    if (isReadOnlyMode || isSubmittingReport) return
    setIsSubmittingReport(true)

    try {
      const payload = toInformPayload(newData)
      dispatch(setInformInfoInEvaluations(payload))
      await updateInform(payload)
      dispatch(resetEvaluations())
      dispatch(resetImages())
      history.push('/home')
    } catch (error) {
      console.error('Error al guardar informe:', error)
    } finally {
      setIsSubmittingReport(false)
    }
  }

  const handleDownloadReport = async () => {
    if (!evaluations || !evaluations.id || !pdfDisponible(evaluations)) return
    await openPDF(`estudios/descargar/${evaluations.id}`)
  }

  if (user.status === USER_STATUS.LOCKED || user.role === 'technical') {
    return <Redirect to='/home' />
  }

  const eyesInRisk = qualityRiskEyes.map((riskEye) => riskEye.eyeLabel)
  const eyeListText = formatEyeList(eyesInRisk)
  const qualityRiskDescription = `Todas las imágenes ${eyeListText} fueron detectadas como de mala calidad por la IA. Sin embargo, no indicaste esto como un error ni solicitaste recapturar las imágenes. Informar en estas condiciones no es recomendable. Si igualmente decidís enviar el informe sin recaptura, dejá una justificación explícita, indicando que la IA etiquetó mal la calidad cuando corresponda, y reportá manualmente esos errores (${qualityRiskManualCount}/${qualityRiskImagesCount} reportadas).`

  const patientDataToUse = evaluations?.patientInfo || (user.role === 'patient' ? user.data : null)
  const showClinicalTabDecision = true

  return (
    <Container>
      {isSubmittingReport && <LoadingScreen variant='overlay' message='Enviando informe...' />}

      <GenericModal
        isOpen={showQualityRiskModal}
        onClose={handleCloseQualityRiskModal}
        withInput
        title='Imágenes de mala calidad detectadas'
        description={qualityRiskDescription}
        inputTitle='Justificación (requerida):'
        placeholder='Describí por qué se envía el informe sin recomendar recaptura...'
        onSave={handleSendWithQualityRisk}
        saveButtonText='Enviar informe'
        cancelButtonText='Volver'
        maxLength={500}
      />

      {!isReadOnlyMode && (
        <NewReportModal ref={modalRef} close={closeModal} modalType={modalType} actionType={modalAction} />
      )}

      {evaluations && <CaseId> {STUDY_TYPE[evaluations.tipo]}/Estudio_{evaluations.id} </CaseId>}
      <PatientInformation study={evaluations} patientData={patientDataToUse} showContactData={false} />

      {reportSended && (
        <ReportSended />
      )}

      {!reportSended && (
        <ReportWrapper>
          {patientPresentationMode === 'not-referred-message' && (
            <ReadOnlyMessageContainer>
              Este estudio no fue referido para informe profesional
            </ReadOnlyMessageContainer>
          )}
          {patientPresentationMode === 'professional-report-pending-message' && (
            <ReadOnlyMessageContainer>
              Este estudio está siendo informado por un profesional. Lo notificaremos cuando el informe esté listo.
            </ReadOnlyMessageContainer>
          )}
          {patientPresentationMode !== 'not-referred-message' && patientPresentationMode !== 'professional-report-pending-message' && (
            <OphthalmologyReportForm
              formData={newData}
              onFormChange={setNewData}
              setEmptyField={setEmptyField}
              activeStudyTab={selectedStudyTab}
              studyType={evaluations && evaluations.tipo}
              readOnly={isReadOnlyMode}
            />
          )}
          {reportBootstrapReady
            ? (
              <GenerateReport
                save={handleSaveReport}
                warningAction={handleWarningAction}
                emptyField={emptyField}
                readOnly={isReadOnlyMode}
                isSubmittingReport={isSubmittingReport}
                onDownloadReport={handleDownloadReport}
                study={evaluations}
                patientData={evaluations && evaluations.patientInfo}
                showClinicalTab={showClinicalTabDecision}
                onTabChange={setSelectedStudyTab}
                manualImageQuality={newData && newData.calidad_imagenes}
                onManualImageQualityChange={handleManualImageQualityChange}
                enableManualQualitySelection={!isReadOnlyMode}
              />
              )
            : (
              <div style={{ width: '55%', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '220px' }}>
                <LoadingScreen message='Cargando imágenes del estudio. Por favor espere.' />
              </div>
              )}
        </ReportWrapper>
      )}
    </Container>
  )
}

export default NewReport
